import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Alert, ProgressBar, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { taskService, departmentService, userService } from '../../services/api';
import EmployeeDashboard from './EmployeeDashboard';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import './Dashboard.css';

// Register Chart.js components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taskCompletionRate, setTaskCompletionRate] = useState(0);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [success, setSuccess] = useState(null);
  
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // Fetch all tasks
      const tasksResponse = await taskService.getAllTasks();
      const tasksData = tasksResponse.data?.data || [];  // Access the data array from the response
      setTasks(tasksData);
      
      // Calculate completion rate
      const completedCount = tasksData.filter(task => task.status === 'completed').length;
      const completionRate = tasksData.length > 0 
        ? Math.round((completedCount / tasksData.length) * 100) 
        : 0;
      setTaskCompletionRate(completionRate);

      // Calculate overdue tasks
      const today = new Date();
      const overdue = tasksData.filter(task => {
        if (!task.due_date) return false;
        return new Date(task.due_date) < today && task.status !== 'completed';
      });
      setOverdueTasks(overdue);

      // Fetch other data
      const [deptResponse, usersResponse, approvalsResponse] = await Promise.all([
        departmentService.getAllDepartments(),
        userService.getAllUsers(),
        taskService.getPendingApprovals()
      ]);

      const departmentsData = deptResponse.data?.data || [];
      const usersData = usersResponse.data?.data || [];
      const approvalsData = approvalsResponse.data?.data || [];

      setDepartments(departmentsData);
      setUsers(usersData);
      setPendingApprovals(approvalsData);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
      // Clear error after 3 seconds
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTask = async (taskId) => {
    try {
      setError(null);
      setLoading(true);

      // First verify the task exists and is still awaiting approval
      const taskToApprove = pendingApprovals.find(task => task.id === taskId);
      if (!taskToApprove) {
        throw new Error('Task not found in pending approvals');
      }

      // Verify task status before proceeding
      try {
        const taskResponse = await taskService.getTaskById(taskId);
        const taskData = taskResponse.data?.data;
        
        if (!taskData) {
          throw new Error('Task not found');
        }
        
        if (taskData.status !== 'awaiting_approval') {
          // Task was already approved or status changed
          setPendingApprovals(prev => prev.filter(task => task.id !== taskId));
          setSuccess('Task was already approved');
          setTimeout(() => setSuccess(null), 3000);
          await fetchDashboardData();
          return;
        }
      } catch (verifyError) {
        console.error('Error verifying task:', verifyError);
        throw new Error('Unable to verify task status');
      }

      try {
        // Make the API call to approve
        await taskService.approveTask(taskId);
        
        // Update tasks list after successful API call
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === taskId 
              ? { ...task, status: 'completed' }
              : task
          )
        );

        // Remove from pending approvals
        setPendingApprovals(prev => 
          prev.filter(task => task.id !== taskId)
        );

        // Show success message
        setSuccess('Task approved successfully');
        setTimeout(() => setSuccess(null), 3000);

      } catch (approveError) {
        console.error('Error in approve API call:', approveError);
        
        // Check if task was actually approved despite the error
        try {
          const checkResponse = await taskService.getTaskById(taskId);
          const checkData = checkResponse.data?.data;
          
          if (checkData && checkData.status === 'completed') {
            // Task was actually approved
            setTasks(prevTasks => 
              prevTasks.map(task => 
                task.id === taskId 
                  ? { ...task, status: 'completed' }
                  : task
              )
            );
            setPendingApprovals(prev => 
              prev.filter(task => task.id !== taskId)
            );
            setSuccess('Task was approved successfully');
            setTimeout(() => setSuccess(null), 3000);
            return;
          }
        } catch (checkError) {
          console.error('Error checking task status:', checkError);
        }
        
        // If we get here, the task wasn't approved
        throw new Error('Failed to approve task. Please try again.');
      }

      // Refresh dashboard data
      await fetchDashboardData();

    } catch (err) {
      console.error('Error in task approval process:', err);
      setError(err.message || 'An error occurred while approving the task');
      setTimeout(() => setError(null), 3000);
      
      // Refresh dashboard to ensure we have the latest state
      await fetchDashboardData();
    } finally {
      setLoading(false);
    }
  };

  const handleRejectTask = async (taskId) => {
    try {
      setError(null);
      // First make the API call
      const response = await taskService.rejectTask(taskId);
      console.log('Reject task response:', response);

      // Update tasks list
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { ...task, status: 'rejected' }
            : task
        )
      );

      // Update pending approvals list
      setPendingApprovals(prevApprovals => 
        prevApprovals.filter(task => task.id !== taskId)
      );

      // Show success message
      setSuccess('Task rejected successfully');
      setTimeout(() => setSuccess(null), 3000);

      // Refresh data to ensure everything is in sync
      fetchDashboardData();
    } catch (err) {
      console.error('Error rejecting task:', err);
      setError(err.response?.data?.message || 'Failed to reject task. Please try again.');
    }
  };

  // Filter tasks by status
  const pendingTasks = tasks.filter(task => task.status === 'pending');
  const inProgressTasks = tasks.filter(task => task.status === 'in_progress');
  const completedTasks = tasks.filter(task => task.status === 'completed');
  const awaitingApprovalTasks = tasks.filter(task => task.status === 'awaiting_approval');
  
  // Filter tasks by priority
  const urgentTasks = tasks.filter(task => task.priority === 'urgent');
  const highPriorityTasks = tasks.filter(task => task.priority === 'high');

  // Get tasks due soon (within 3 days)
  const today = new Date();
  const threeDaysFromNow = new Date();
  threeDaysFromNow.setDate(today.getDate() + 3);
  
  const tasksDueSoon = tasks.filter(task => {
    if (!task.due_date) return false;
    const dueDate = new Date(task.due_date);
    return dueDate >= today && dueDate <= threeDaysFromNow;
  });

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'in_progress': return 'primary';
      case 'awaiting_approval': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'low': return 'info';
      case 'medium': return 'secondary';
      case 'high': return 'warning';
      case 'urgent': return 'danger';
      default: return 'secondary';
    }
  };

  if (loading) {
    return <div className="text-center p-5">Loading dashboard data...</div>;
  }

  // Prepare data for charts
  const statusData = [
    { name: 'Pending', value: pendingTasks.length, color: '#6c757d' },
    { name: 'In Progress', value: inProgressTasks.length, color: '#0d6efd' },
    { name: 'Completed', value: completedTasks.length, color: '#198754' },
  ];

  const priorityData = [
    { name: 'Low', value: tasks.filter(t => t.priority === 'low').length, color: '#0dcaf0' },
    { name: 'Medium', value: tasks.filter(t => t.priority === 'medium').length, color: '#6c757d' },
    { name: 'High', value: highPriorityTasks.length, color: '#ffc107' },
    { name: 'Urgent', value: urgentTasks.length, color: '#dc3545' },
  ];
  
  // Chart data and options
  const statusChartData = {
    labels: statusData.map(item => item.name),
    datasets: [
      {
        data: statusData.map(item => item.value),
        backgroundColor: statusData.map(item => item.color),
        borderColor: statusData.map(item => item.color),
        borderWidth: 1,
      },
    ],
  };

  const statusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const priorityChartData = {
    labels: priorityData.map(item => item.name),
    datasets: [
      {
        label: 'Tasks by Priority',
        data: priorityData.map(item => item.value),
        backgroundColor: priorityData.map(item => item.color),
        borderColor: priorityData.map(item => item.color),
        borderWidth: 1,
      },
    ],
  };

  const priorityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  // Function to render activity icon based on type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'task_created': return '📝';
      case 'task_completed': return '✅';
      case 'comment_added': return '💬';
      case 'user_assigned': return '👤';
      default: return '🔔';
    }
  };

  const renderTaskCard = (task) => (
    <Card key={task.id} className="task-card mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <Card.Title className="mb-0">{task.title}</Card.Title>
          <Badge bg={task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'warning' : 'info'}>
            {task.priority}
          </Badge>
        </div>
        
        <Card.Text>{task.description || 'No description provided.'}</Card.Text>
        
        {task.due_date && (
          <div className="mb-2">
            <small className="text-muted">
              Due: {new Date(task.due_date).toLocaleDateString()}
            </small>
          </div>
        )}
        
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            <Badge bg={
              task.status === 'completed' ? 'success' :
              task.status === 'awaiting_approval' ? 'warning' :
              task.status === 'in_progress' ? 'primary' : 'secondary'
            }>
              {task.status === 'awaiting_approval' ? 'Awaiting Approval' : 
               task.status === '' || task.status === 'pending' ? 'To Do' : 
               task.status.replace('_', ' ')}
            </Badge>
            {task.assigned_to && (
              <small className="text-muted ms-2">
                Assigned to: {task.assigned_to}
              </small>
            )}
          </div>
          
          {task.status === 'awaiting_approval' && (
            <div>
              <Button 
                variant="success" 
                size="sm" 
                className="me-2"
                onClick={() => handleApproveTask(task.id)}
              >
                <i className="fas fa-check me-1"></i> Approve
              </Button>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container fluid className="dashboard-container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="dashboard-title">Welcome to Streamline </h2>
        <div>
          <Button 
            variant="outline-primary" 
            className="me-2 refresh-btn"
            onClick={() => {
              setLoading(true);
              fetchDashboardData();
            }}
          >
            <i className="fas fa-sync-alt me-1"></i> Refresh
          </Button>
          <Button variant="primary" as={Link} to="/tasks/new" className="new-task-btn">
            <i className="fas fa-plus me-1"></i> New Task
          </Button>
        </div>
      </div>
      
      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
      {success && <Alert variant="success" className="mb-4">{success}</Alert>}
      
      {/* Pending Approvals Section */}
      {pendingApprovals.length > 0 && (
        <Card className="approval-card mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <i className="fas fa-clock me-2"></i>
              Tasks Awaiting Approval
            </div>
            <Badge bg="light" text="dark" pill>{pendingApprovals.length}</Badge>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="list-group list-group-flush">
              {pendingApprovals.map(task => (
                <div key={task.id} className="list-group-item task-item priority-{task.priority}">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="task-title mb-1">
                        <span className="priority-indicator priority-{task.priority}"></span>
                        {task.title}
                      </h6>
                      <div className="d-flex align-items-center small text-muted">
                        <span className="me-3">
                          <i className="fas fa-user me-1"></i> {task.assigned_to_name || 'Unassigned'}
                        </span>
                        <span>
                          <i className="fas fa-calendar me-1"></i> Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'Not set'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Button 
                        variant="success" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleApproveTask(task.id)}
                      >
                        <i className="fas fa-check me-1"></i> Approve
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => handleRejectTask(task.id)}
                      >
                        <i className="fas fa-times me-1"></i> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Dashboard Metrics */}
      <Row className="g-4 mb-4">
        <Col md={3}>
          <Card className="metric-card total-tasks h-100">
            <Card.Body>
              <div className="metric-icon">
                <i className="fas fa-tasks"></i>
              </div>
              <h6 className="text-uppercase mb-2">Total Tasks</h6>
              <h1 className="display-4 mb-0">{tasks.length}</h1>
              <div className="mt-3 small">
                <span className="text-white">
                  <i className="fas fa-users me-1"></i> {users.length} Users
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3}>
          <Card className="metric-card completion-rate h-100">
            <Card.Body>
              <div className="metric-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h6 className="text-uppercase mb-2">Completion Rate</h6>
              <h1 className="display-4 mb-0">{taskCompletionRate}%</h1>
              <div className="mt-3">
                <ProgressBar 
                  now={taskCompletionRate} 
                  variant="light" 
                  className="progress-bar-thick"
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3}>
          <Card className="metric-card overdue-tasks h-100">
            <Card.Body>
              <div className="metric-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h6 className="text-uppercase mb-2">Overdue Tasks</h6>
              <h1 className="display-4 mb-0">{overdueTasks.length}</h1>
              <div className="mt-3 small">
                {overdueTasks.length > 0 ? (
                  <span className="text-white">
                    <i className="fas fa-clock me-1"></i> Requires attention
                  </span>
                ) : (
                  <span className="text-white">
                    <i className="fas fa-check-circle me-1"></i> No overdue tasks
                  </span>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3}>
          <Card className="metric-card due-soon h-100">
            <Card.Body>
              <div className="metric-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <h6 className="text-uppercase mb-2">Tasks Due Soon</h6>
              <h1 className="display-4 mb-0">{tasksDueSoon.length}</h1>
              <div className="mt-3 small">
                <span className="text-white">
                  <i className="fas fa-hourglass-half me-1"></i> Next 3 days
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Main content area */}
      <Row className="g-4">
        {/* Left Column */}
        <Col md={6}>
          {/* Status Breakdown */}
          <Card className="border-0 shadow-lg rounded-lg chart-card mb-4">
            <Card.Header className="chart-header py-3">
              <h5 className="m-0">Task Status Breakdown</h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-container" style={{ position: 'relative', height: '350px', width: '100%' }}>
                {tasks.length > 0 ? (
                  <Pie data={statusChartData} options={statusChartOptions} />
                ) : (
                  <div className="text-center p-5 text-muted">
                    <p>No task data available for visualization.</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Right Column */}
        <Col md={6}>
          {/* Priority Breakdown */}
          <Card className="border-0 shadow-lg rounded-lg chart-card mb-4">
            <Card.Header className="chart-header py-3">
              <h5 className="m-0">Task Priority Breakdown</h5>
            </Card.Header>
            <Card.Body>
              <div className="chart-container" style={{ position: 'relative', height: '350px', width: '100%' }}>
                {tasks.length > 0 ? (
                  <Bar data={priorityChartData} options={priorityChartOptions} />
                ) : (
                  <div className="text-center p-5 text-muted">
                    <p>No task data available for visualization.</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Quick Actions */}
      <Row className="mt-4">
        <Col md={12}>
          <Card className="border-0 shadow-lg rounded-lg">
            <Card.Header className="py-3 bg-light">
              <h5 className="m-0">Quick Actions</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="d-flex flex-wrap">
                <Button 
                  as={Link} 
                  to="/tasks/new" 
                  variant="light" 
                  className="quick-action-btn m-2 d-flex flex-column align-items-center p-4"
                >
                  <div className="action-icon mb-2">
                    <i className="fas fa-plus-circle"></i>
                  </div>
                  <span>Create Task</span>
                </Button>
                
                <Button 
                  as={Link} 
                  to="/tasks" 
                  variant="light" 
                  className="quick-action-btn m-2 d-flex flex-column align-items-center p-4"
                >
                  <div className="action-icon mb-2">
                    <i className="fas fa-list"></i>
                  </div>
                  <span>View All Tasks</span>
                </Button>
                
                <Button 
                  as={Link} 
                  to="/users" 
                  variant="light" 
                  className="quick-action-btn m-2 d-flex flex-column align-items-center p-4"
                >
                  <div className="action-icon mb-2">
                    <i className="fas fa-users"></i>
                  </div>
                  <span>Manage Users</span>
                </Button>
                
                <Button 
                  as={Link} 
                  to="/departments" 
                  variant="light" 
                  className="quick-action-btn m-2 d-flex flex-column align-items-center p-4"
                >
                  <div className="action-icon mb-2">
                    <i className="fas fa-building"></i>
                  </div>
                  <span>Departments</span>
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const Dashboard = () => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return (
      <Container className="text-center py-5">
        <Alert variant="warning">
          Please log in to access the dashboard.
        </Alert>
        <Link to="/login" className="btn btn-primary">Go to Login</Link>
      </Container>
    );
  }

  return currentUser.role === 'employee' ? <EmployeeDashboard /> : <AdminDashboard />;
};

export default Dashboard;
